import { NavLink, useLocation } from "react-router-dom";
import { Box, Divider, Typography } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

import {
    FileStack,
    Layers,
    LayoutDashboard,
    Store,
    ShieldHalf,
    Logs,
    Fuel,
    BarChartBig,
    Bell,
    ArrowLeft,
    Plus,
    CalendarRange,
    Settings2,
    Tag,
    LifeBuoy,
} from "lucide-react";

import { IconButton } from "../../shared";
import "./index.scss";
import { useProject } from "../../hooks/query";
import { IProjectList } from "../../interfaces";
import { usePastelColors } from "../../hooks";
import { Link } from "react-router-dom";

export default function NavigationPanel({ collapsed }: { collapsed?: boolean }) {
    const pathname = useLocation();
    const isAdminRoute = pathname.pathname.startsWith("/admin");

    const panelVariants = {
        initial: { x: "-40px", opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: "40px", opacity: 0 },
    };

    return (
        <Box
            className={`NavigationWrapper ${collapsed ? "collapsed" : ""}`}
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
            {/* Branding Section */}
            <Box sx={{ p: collapsed ? 1 : 2, pb: 0, textAlign: collapsed ? "center" : "left" }}>
                <Box display="flex" alignItems="center" justifyContent={collapsed ? "center" : "flex-start"} gap={0.5}>
                    <Typography
                        level="h4"
                        sx={{ fontFamily: "Merriweather", fontSize: collapsed ? "1.2rem" : undefined, display: "flex" }}
                    >
                        R
                        <AnimatePresence mode="wait">
                            {!collapsed && (
                                <motion.span
                                    initial={{ opacity: 0, width: 0 }}
                                    animate={{ opacity: 1, width: "auto" }}
                                    exit={{ opacity: 0, width: 0 }}
                                    style={{ overflow: "hidden", whiteSpace: "nowrap" }}
                                >
                                    oofinMotion
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </Typography>
                </Box>
                <AnimatePresence>
                    {!collapsed && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                        >
                            <Typography level="body-xs" sx={{ marginTop: -0.5, fontSize: 13 }}>
                                Growth through efficiency
                            </Typography>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>

            {/* Scrollable Nav Section */}
            <Box sx={{ flex: 1, overflow: "auto" }}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={String(isAdminRoute)}
                        variants={panelVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{
                            type: "tween",
                            duration: 0.2,
                            ease: "easeInOut",
                        }}
                    >
                        {isAdminRoute ? (
                            <NavigationPanelAdmin collapsed={collapsed} />
                        ) : (
                            <NavigationPanelMenu collapsed={collapsed} />
                        )}
                    </motion.div>
                </AnimatePresence>
            </Box>

            {/* Bottom Support Section */}
            <Box sx={{ p: collapsed ? 1 : 2, borderTop: "1px solid", borderColor: "divider", mt: "auto" }}>
                <NavigationItem
                    name={collapsed ? "Help" : "Help & Documentation"}
                    href="/help"
                    icon={LifeBuoy}
                    collapsed={collapsed}
                />
            </Box>
        </Box>
    );
}

function NavigationPanelAdmin({ collapsed }: { collapsed?: boolean }) {
    return (
        <Box>
            <Box mt={3} px={collapsed ? 0.5 : 2} pb={2} display={"flex"} flexDirection={"column"} gap={0.5}>
                <Link to={"/"}>
                    <Box
                        display={"flex"}
                        flexDirection={collapsed ? "column" : "row"}
                        alignItems={"center"}
                        justifyContent={collapsed ? "center" : "flex-start"}
                        ml={0.5}
                        mb={2}
                        gap={0.5}
                        color={"primary"}
                    >
                        <ArrowLeft size={16} color="currentColor" />
                        <AnimatePresence>
                            {!collapsed && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -10 }}
                                >
                                    <Typography typography={"body-sm"} sx={{ fontWeight: 600, color: "primary" }}>
                                        Home
                                    </Typography>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>
                </Link>
                {adminMenus.map((e: any) => {
                    return (
                        <Box key={e.category}>
                            <AnimatePresence>
                                {!collapsed && (
                                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                mb: 1,
                                                ml: 1,
                                                mt: 1,
                                                textTransform: "uppercase",
                                                color: "text.primary",
                                            }}
                                            level="body-xs"
                                            gutterBottom
                                        >
                                            {e.category}
                                        </Typography>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            {e.menu.map((menuItem: any) => (
                                <NavigationItem {...menuItem} key={menuItem.name} collapsed={collapsed} />
                            ))}

                            <Divider sx={{ mt: 2, mb: 1 }} />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function NavigationPanelMenu({ collapsed }: { collapsed?: boolean }) {
    const { useList } = useProject();
    const projectListSvc = useList();

    const [projects, setProjects] = useState<IProjectList[]>([]);

    const fetchData = async () => {
        try {
            const response = await projectListSvc.mutateAsync();
            setProjects(response);
        } catch (e) {
            console.error("Error fetching data", e);
        }
    };

    useEffect(() => {
        fetchData().then();
    }, []);

    return (
        <Box sx={{ overflow: "hidden" }}>
            <Box
                mt={collapsed ? 2 : 6}
                px={collapsed ? 1 : 2}
                pb={2}
                display={"flex"}
                flexDirection={"column"}
                gap={1.5}
            >
                {menus.map((e: any) => {
                    return <NavigationItem {...e} key={e.name} collapsed={collapsed} />;
                })}
            </Box>
            <AnimatePresence>
                {!collapsed && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                    >
                        <Divider sx={{ mt: 2 }} />
                        <Box
                            px={2.5}
                            pt={3}
                            display={"flex"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            pb={1}
                        >
                            <Typography sx={{ color: "text.primary", fontWeight: 500 }} level="body-sm">
                                Projects
                            </Typography>
                            <IconButton
                                color={"neutral"}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 8,
                                    "--IconButton-size": "22px",
                                }}
                            >
                                <Plus size={"14px"} color="currentColor" />
                            </IconButton>
                        </Box>
                        <Box px={2} pt={0} display={"flex"} flexDirection={"column"} gap={1} flex={1} overflow={"auto"}>
                            {projects.slice(0, 10).map((project: any) => {
                                return <ProjectItems {...project} key={project.Id} />;
                            })}
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

function ProjectItems(props: any) {
    const { Color, Name, Id }: any = { ...props };
    const pasterColors = usePastelColors(Id);
    const color = Color ? Color : pasterColors;

    return (
        <Link to={`/project/list/${Id}`}>
            <Box display={"flex"} alignItems={"center"} gap={2} py={1} ml={1} mr={2}>
                <Box sx={{ background: color, height: 10, width: 10, borderRadius: 3 }}></Box>
                <Typography
                    level={"body-sm"}
                    sx={{
                        color: "inherit",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {Name}
                </Typography>
            </Box>
        </Link>
    );
}
function NavigationItem(props: any) {
    const { name, href, icon: Icon, collapsed }: any = { ...props };
    const pathname = useLocation();

    // If the href is '/' (root route), handle it separately
    const isRoot = href === "/";
    const activeLink = isRoot ? pathname === href : pathname.pathname.startsWith(href);

    return (
        <NavLink
            to={href}
            className={`navigation-item ${activeLink ? "active" : ""} ${collapsed ? "collapsed-item" : ""}`}
        >
            <Box
                display={"flex"}
                flexDirection={collapsed ? "column" : "row"}
                alignItems={"center"}
                justifyContent={collapsed ? "center" : "flex-start"}
                gap={collapsed ? 0.5 : 1}
                sx={activeLink ? { color: "white" } : {}}
            >
                <Icon size={collapsed ? 20 : 16} />
                <Typography
                    sx={{
                        color: "inherit",
                        fontWeight: 500,
                        fontSize: collapsed ? "11px" : "body-sm",
                        textAlign: "center",
                        transition: "all 0.3s ease",
                    }}
                    level={collapsed ? undefined : "body-sm"}
                >
                    {name}
                </Typography>
            </Box>
        </NavLink>
    );
}

const menus = [
    {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
    },
    {
        name: "Projects",
        href: "/project",
        icon: Layers,
    },
    {
        name: "Reports",
        href: "/reports/list",
        icon: FileStack,
    },
    // {
    //     name: "Inventory",
    //     href: "/inventory/list",
    //     icon: ShoppingBasket,
    // },
    {
        name: "Suppliers",
        href: "/suppliers",
        icon: Store,
    },
    {
        name: "Calendar",
        href: "/calendar",
        icon: CalendarRange,
    },
];

const adminMenus = [
    {
        category: "Platform",
        menu: [
            {
                name: "Manufacturers",
                href: "/admin/manufacture",
                icon: Layers,
            },
            {
                name: "Product Categories",
                href: "/admin/product-category",
                icon: Tag,
            },
            {
                name: "General",
                href: "/admin/general",
                icon: Settings2,
            },
        ],
    },
    {
        category: "User Management",
        menu: [
            {
                name: "Users",
                href: "/admin/users",
                icon: FileStack,
            },
            {
                name: "Roles",
                href: "/admin/roles",
                icon: ShieldHalf,
            },
        ],
    },
    {
        category: "Advanced",
        menu: [
            {
                name: "Audit Logs",
                href: "/admin/audit-logs",
                icon: Logs,
            },
            {
                name: "Billing",
                href: "/admin/billing",
                icon: Fuel,
            },
            {
                name: "Notifications",
                href: "/admin/billing",
                icon: Bell,
            },
        ],
    },
    {
        category: "Reports",
        menu: [
            {
                name: "Analytics",
                href: "/admin/reports",
                icon: BarChartBig,
            },
        ],
    },
];
